import { Button } from "@pepperstone/pepper-ui-kit";
import { download, generateCsv, mkConfig } from "export-to-csv";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import type {
  CashierTradingAccount,
  CashierTransactionView,
  EnumPaymentType,
  EnumCashierTxStatus,
} from "@/api/cashier";
import { useCurrencies } from "@/hooks/deposit/useCurrencies";
import { useFormatDate } from "@/hooks/useFormatDate";
import styles from "./Transactions.module.css";
import { moneyToMajorUnits } from "@/utils/helpers";

/**
 * Helper function to generate a CSV filename based on the current date and time
 * @param date - The current date
 * @returns A formatted filename string: transaction_history_DDMMYYYY_HHMM
 */
const generateCsvFilename = (date: Date) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `transaction_history_${day}${month}${year}_${hours}${minutes}`;
};

/**
 * The handleExport function generates and downloads a CSV file containing transaction data.
 * It maps the transaction data to a format suitable for CSV export, including translating
 * certain fields using the provided translation function.
 * @param exportData - An array of CashierTransactionView objects to be exported.
 * @param tradingAccounts - An array of CashierTradingAccount objects representing trading accounts.
 */
const useHandleExport = (
  exportData: CashierTransactionView[],
  tradingAccounts: CashierTradingAccount[]
) => {
  const { t } = useTranslation();
  const { currencies } = useCurrencies();
  const { formatDate } = useFormatDate();

  return useCallback(() => {
    const typeMap: Record<EnumPaymentType, string> = {
      deposit: t("transactions.history.table.types.deposit"),
      transfer: t("transactions.history.table.types.transfer"),
      withdrawal: t("transactions.history.table.types.withdrawal"),
      cashAdjustment: t("transactions.history.table.types.cash_adjustment"),
    };

    const statusMap: Record<EnumCashierTxStatus, string> = {
      Requested: t("transactions.history.status.requested"),
      Pending: t("transactions.history.status.pending"),
      Processing: t("transactions.history.status.processing"),
      Complete: t("transactions.history.status.complete"),
      Unsuccessful: t("transactions.history.status.unsuccessful"),
      Cancelled: t("transactions.history.status.cancelled"),
    };
    const csvConfig = mkConfig({
      useKeysAsHeaders: true,
      filename: generateCsvFilename(new Date()),
    });
    const csv = generateCsv(csvConfig)(
      exportData.map((transaction: CashierTransactionView) => {
        const currencyPrimitive = currencies?.[transaction.money.code];

        const fromAccountObj = transaction.from_trading_account
          ? tradingAccounts.find(
              ({ id }) => id === transaction.from_trading_account
            )
          : null;
        const fromAccount = fromAccountObj
          ? fromAccountObj.accountLoginId
          : transaction.saved_payment_account?.detail ||
            t("transactions.history.table.admin");
        const toAccountObj = transaction.to_trading_account
          ? tradingAccounts.find(
              ({ id }) => id === transaction.to_trading_account
            )
          : null;
        const toAccount = toAccountObj
          ? toAccountObj.accountLoginId
          : transaction.saved_payment_account?.detail || "";

        return {
          Time: formatDate(transaction.created_at),
          Type: typeMap[transaction.type],
          "Order number": transaction.reference_id,
          From: fromAccount,
          To: toAccount,
          "Payment Method": transaction.saved_payment_account?.name,
          Amount: moneyToMajorUnits(transaction.money) ?? 0,
          "Amount currency": currencyPrimitive?.code,

          Fee: moneyToMajorUnits(transaction.fee_money) ?? 0,
          "Fee currency": transaction.fee_money?.code,

          Status: statusMap[transaction.status],
        };
      })
    );
    download(csvConfig)(csv);
  }, [t, currencies, formatDate, exportData, tradingAccounts]);
};

/**
 * Component for exporting transaction data to a CSV file
 * @param exportData - An array of CashierTransactionView objects to be exported
 * @param tradingAccounts - An array of CashierTradingAccount objects representing trading accounts
 * @returns A button that, when clicked, generates and downloads a CSV file of the transaction data
 */
export const TransactionExportButton = ({
  exportData,
  tradingAccounts,
  isLoading,
}: {
  exportData: CashierTransactionView[];
  tradingAccounts: CashierTradingAccount[];
  isLoading: boolean;
}) => {
  const { t } = useTranslation();
  const handleExport = useHandleExport(exportData, tradingAccounts);

  return (
    <Button
      type="button"
      size="ps-btn-md"
      variant="ps-btn-primary"
      onClick={handleExport}
      className={styles.exportButton}
      disabled={exportData.length === 0}
      isLoading={isLoading}
    >
      {t("transactions.history.export")}
    </Button>
  );
};
